'use client';

import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import EventPing from '@/components/EventPing';
import helpData from '@/lib/help-center.json';

interface HelpLangContent {
  title: string;
  entry: string;
  summary: string;
  steps: string[];
  faq: string[];
}
interface SignalTypeGroup {
  group: string;
  items: string[];
}
interface HelpCardData {
  family: string;
  familyZhHant: string;
  familyEn: string;
  'zh-CN'?: HelpLangContent;
  'zh-Hant': HelpLangContent;
  en: HelpLangContent;
  signalTypes?: { 'zh-Hant': SignalTypeGroup[]; en: SignalTypeGroup[] };
}
interface HelpFamily {
  id: string;
  'zh-CN': string;
  'zh-Hant': string;
  en: string;
}
interface HelpData {
  families: HelpFamily[];
  cards: HelpCardData[];
}

const data = helpData as unknown as HelpData;

export default function HelpPage() {
  const { language } = useAppStore();
  const t = translate(language);
  const langKey = language === 'en' ? 'en' : 'zh-Hant';

  const [activeFamily, setActiveFamily] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [allOpen, setAllOpen] = useState(false);

  // 上下文入口支持：/help?f=<family>&card=<title> —— 定位功能族并自动展开对应卡片
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const f = q.get('f');
    const card = q.get('card');
    if (f) setActiveFamily(f);
    if (card) {
      const target = decodeURIComponent(card);
      const match = data.cards.find((c) => c[langKey].title === target || c[langKey].title.includes(target));
      if (match) {
        const key = cardKey(match, langKey);
        setActiveFamily(match.family);
        setExpanded((prev) => new Set(prev).add(key));
        setTimeout(() => {
          document.getElementById(`help-card-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardKey = (c: HelpCardData, lk: 'en' | 'zh-Hant') => `${c.family}:${c[lk].title}`;

  const families = useMemo(() => {
    const all: HelpFamily = { id: 'all', 'zh-CN': t.help.allFamilies, 'zh-Hant': t.help.allFamilies, en: t.help.allFamilies };
    return [all, ...data.families];
  }, [t.help.allFamilies]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.cards.filter((c) => {
      if (activeFamily !== 'all' && c.family !== activeFamily) return false;
      if (!q) return true;
      const lc = c[langKey];
      return (
        lc.title.toLowerCase().includes(q) ||
        lc.summary.toLowerCase().includes(q) ||
        lc.entry.toLowerCase().includes(q)
      );
    });
  }, [activeFamily, query, langKey]);

  const familyName = (id: string) => {
    if (id === 'all') return t.help.allFamilies;
    const f = data.families.find((x) => x.id === id);
    return f ? f[langKey] : id;
  };

  const toggleCard = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setAllOpen((v) => {
      const next = !v;
      setExpanded(next ? new Set(filtered.map((c) => cardKey(c, langKey))) : new Set());
      return next;
    });
  };

  return (
    <div className="mx-auto min-h-[calc(100vh-4rem)] max-w-[1440px] px-3 py-6 sm:px-4 lg:px-6">
      <EventPing event="help_view" />
      {/* 头部 */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gf-text sm:text-2xl">{t.help.title}</h1>
        <p className="mt-1 text-sm text-gf-muted">{t.help.subtitle}</p>
      </div>

      {/* 搜索 */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gf-muted"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.help.searchPlaceholder}
            className="h-10 w-full rounded-lg border border-gf-border bg-gf-surface2 pl-9 pr-3 text-sm text-gf-text outline-none transition-colors placeholder:text-gf-muted focus:border-gf-accent/60"
            aria-label={t.help.searchPlaceholder}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleAll}
            className="h-10 rounded-lg border border-gf-border bg-gf-surface2 px-3 text-xs text-gf-text transition-colors hover:border-gf-accent/50"
          >
            {allOpen ? t.help.collapseAll : t.help.expandAll}
          </button>
          <span className="text-xs text-gf-muted">
            {filtered.length} {t.help.cardCount}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* 功能族导航（桌面左侧 / 手机横向 chips） */}
        <aside className="lg:w-48 lg:shrink-0">
          <div className="flex gap-1.5 overflow-x-auto pb-1 lg:sticky lg:top-20 lg:flex-col lg:overflow-visible lg:pb-0">
            {families.map((f) => {
              const active = activeFamily === f.id;
              const count = f.id === 'all' ? data.cards.length : data.cards.filter((c) => c.family === f.id).length;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActiveFamily(f.id)}
                  className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors lg:justify-between ${
                    active
                      ? 'bg-gf-accent/10 font-semibold text-gf-accent'
                      : 'text-gf-muted hover:bg-gf-surface2 hover:text-gf-text'
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <span>{f[langKey]}</span>
                  <span className={`text-[10px] ${active ? 'text-gf-accent/70' : 'text-gf-muted/60'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* 卡片列表 */}
        <main className="min-w-0 flex-1">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-gf-border bg-gf-surface px-4 py-10 text-center text-sm text-gf-muted">
              {t.help.noResults}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((c) => {
                const lc = c[langKey];
                const key = cardKey(c, langKey);
                const open = expanded.has(key);
                return (
                  <section
                    key={key}
                    id={`help-card-${key}`}
                    className={`rounded-xl border bg-gf-surface transition-colors ${
                      open ? 'border-gf-accent/40' : 'border-gf-border'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleCard(key)}
                      className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
                      aria-expanded={open}
                    >
                      <span className="flex-1">
                        <span className="block text-sm font-semibold text-gf-text">{lc.title}</span>
                        <span className="mt-0.5 block text-xs text-gf-muted">{lc.summary}</span>
                      </span>
                      <span className="shrink-0 rounded-md bg-gf-surface2 px-2 py-0.5 text-[10px] text-gf-muted">
                        {familyName(c.family)}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`shrink-0 text-gf-muted transition-transform ${open ? 'rotate-180' : ''}`}
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>

                    {open && (
                      <div className="border-t border-gf-border px-4 py-4">
                        {/* 入口位置 */}
                        <div className="mb-3 flex items-start gap-2 text-xs">
                          <span className="mt-0.5 shrink-0 rounded bg-gf-surface2 px-1.5 py-0.5 font-medium text-gf-muted">
                            {t.help.entry}
                          </span>
                          <span className="text-gf-text">{lc.entry}</span>
                        </div>

                        {/* 信号/预警类型清单（总览卡专用） */}
                        {(() => {
                          const groups = c.signalTypes?.[langKey];
                          if (!groups || groups.length === 0) return null;
                          return (
                            <div className="mb-3">
                              <h3 className="mb-1.5 text-xs font-semibold text-gf-text">{t.help.types}</h3>
                              <div className="flex flex-col gap-2">
                                {groups.map((g, gi) => (
                                  <div key={gi} className="rounded-lg border border-gf-border bg-gf-bg/40 px-3 py-2">
                                    <h4 className="mb-1 text-xs font-semibold text-gf-accent">{g.group}</h4>
                                    <ul className="flex flex-col gap-1">
                                      {g.items.map((item, ii) => (
                                        <li key={ii} className="flex gap-1.5 text-sm leading-relaxed text-gf-text">
                                          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gf-muted/50" />
                                          <span className="min-w-0">{item}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* 操作步骤 */}
                        {lc.steps.length > 0 && (
                          <div className="mb-3">
                            <h3 className="mb-1.5 text-xs font-semibold text-gf-text">{t.help.steps}</h3>
                            <ol className="flex flex-col gap-1.5">
                              {lc.steps.map((step, i) => (
                                <li key={i} className="flex gap-2 text-sm leading-relaxed text-gf-text">
                                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-gf-accent/15 text-[10px] font-semibold text-gf-accent">
                                    {i + 1}
                                  </span>
                                  <span className="min-w-0">{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* FAQ */}
                        {lc.faq.length > 0 && (
                          <div>
                            <h3 className="mb-1.5 text-xs font-semibold text-gf-text">{t.help.faq}</h3>
                            <div className="flex flex-col gap-2">
                              {lc.faq.map((item, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-gf-border bg-gf-bg/40 px-3 py-2 text-sm leading-relaxed text-gf-text"
                                >
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
